import { Schema, NodeSpec, MarkSpec } from "prosemirror-model";

// view_block column config round-trips through DOM as JSON / "true"|"false".
function parseColumnsAttr(raw: string | null): string[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : null;
  } catch {
    return null;
  }
}
function parseBoolAttr(raw: string | null): boolean | null {
  if (raw == null) return null;
  return raw !== "false";
}

const nodes: Record<string, NodeSpec> = {
  doc: { content: "block+" },

  paragraph: {
    content: "inline*",
    group: "block",
    parseDOM: [{ tag: "p" }],
    toDOM: () => ["p", 0],
  },

  heading: {
    content: "inline*",
    group: "block",
    attrs: { level: { default: 1 } },
    defining: true,
    parseDOM: [1, 2, 3, 4].map((level) => ({
      tag: `h${level}`,
      attrs: { level },
    })),
    toDOM: (node) => [`h${node.attrs["level"] as number}`, 0],
  },

  code_block: {
    content: "text*",
    group: "block",
    code: true,
    defining: true,
    marks: "",
    parseDOM: [{ tag: "pre", preserveWhitespace: "full" }],
    toDOM: () => ["pre", ["code", 0]],
  },

  // One list node for all three flavours (bullet / ordered / todo), selected by
  // `style`. `order` only applies to ordered lists. Items are the shared
  // `list_item` node, so nesting, splitting and sink/lift behave identically
  // across all flavours.
  list: {
    content: "list_item+",
    group: "block",
    attrs: { style: { default: "bullet" }, order: { default: 1 } },
    parseDOM: [
      {
        tag: "ul[data-todo]",
        getAttrs: () => ({ style: "todo" }),
      },
      {
        tag: "ol",
        getAttrs: (dom) => ({ style: "ordered", order: (dom as HTMLOListElement).start || 1 }),
      },
      {
        tag: "ul",
        getAttrs: () => ({ style: "bullet" }),
      },
    ],
    toDOM: (node) => {
      const style = node.attrs["style"] as string;
      if (style === "ordered") {
        const order = node.attrs["order"] as number;
        return order === 1 ? ["ol", 0] : ["ol", { start: order }, 0];
      }
      if (style === "todo") return ["ul", { "data-todo": "" }, 0];
      return ["ul", 0];
    },
  },

  // Shared item for every list flavour. `checked` is only meaningful inside a
  // todo list; CSS (and the click handler) scope its checkbox affordance to
  // `ul[data-todo] > li`.
  list_item: {
    content: "paragraph block*",
    attrs: { checked: { default: false } },
    parseDOM: [
      {
        tag: "li",
        getAttrs: (dom) => ({ checked: (dom as HTMLElement).getAttribute("data-checked") === "true" }),
      },
    ],
    toDOM: (node) => ["li", { "data-checked": String(node.attrs["checked"] as boolean) }, 0],
    defining: true,
  },

  view_block: {
    group: "block",
    atom: true,
    // Not node-selectable: arrowing/clicking past it yields a gap cursor (a
    // clear "insert here" caret) instead of a NodeSelection border. Range
    // selections still cover it (highlighted via the block-selection plugin).
    selectable: false,
    attrs: {
      viewType: { default: "list" },
      // filter/orderBy/groupBy are arbitrary CEL expression strings (or null).
      filter: { default: null },
      orderBy: { default: null },
      orderDesc: { default: null },
      groupBy: { default: null },
      // Kanban column display order + whether to show columns beyond that list.
      columns: { default: null },
      showAllColumns: { default: null },
      display: { default: "preview-short" },
    },
    parseDOM: [
      {
        tag: "div[data-view-block]",
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return {
            viewType: el.getAttribute("data-view-type") ?? "list",
            filter: el.getAttribute("data-filter"),
            orderBy: el.getAttribute("data-order-by"),
            orderDesc: parseBoolAttr(el.getAttribute("data-order-desc")),
            groupBy: el.getAttribute("data-group-by"),
            columns: parseColumnsAttr(el.getAttribute("data-columns")),
            showAllColumns: parseBoolAttr(el.getAttribute("data-show-all-columns")),
            display: el.getAttribute("data-display") ?? "preview-short",
          };
        },
      },
    ],
    toDOM: (node) => {
      const a = node.attrs as Record<string, unknown>;
      const attrs: Record<string, string> = { "data-view-block": "" };
      const set = (k: string, v: unknown) => { if (typeof v === "string" && v) attrs[k] = v; };
      set("data-view-type", a["viewType"]);
      set("data-filter", a["filter"]);
      set("data-order-by", a["orderBy"]);
      if (typeof a["orderDesc"] === "boolean") {
        attrs["data-order-desc"] = String(a["orderDesc"]);
      }
      set("data-group-by", a["groupBy"]);
      if (Array.isArray(a["columns"]) && a["columns"].length) {
        attrs["data-columns"] = JSON.stringify(a["columns"]);
      }
      if (typeof a["showAllColumns"] === "boolean") {
        attrs["data-show-all-columns"] = String(a["showAllColumns"]);
      }
      set("data-display", a["display"]);
      return ["div", attrs];
    },
  },

  text: { group: "inline" },

  // Inline link to another card, rendered as the card's title. A section is
  // a separate node (`card_embed`).
  card_link: {
    group: "inline",
    inline: true,
    atom: true,
    attrs: { uuid: {} },
    parseDOM: [
      {
        tag: "a[data-card-link]",
        getAttrs: (dom) => ({
          uuid: (dom as HTMLElement).getAttribute("data-card-link"),
        }),
      },
    ],
    toDOM: (node) => [
      "a",
      { "data-card-link": node.attrs["uuid"] as string, class: "card-link" },
      "…",
    ],
  },

  // Another card, shown as a section that the user can collapse and edit.
  // `height` is null when the section grows to fit the card.
  card_embed: {
    group: "block",
    atom: true,
    // See `view_block`: not node-selectable, so a gap cursor marks the spot
    // before/after it instead of a NodeSelection border.
    selectable: false,
    attrs: { uuid: {}, collapsed: { default: false }, height: { default: null } },
    parseDOM: [
      {
        tag: "div[data-card-embed]",
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          const height = Number(el.getAttribute("data-height"));
          return {
            uuid: el.getAttribute("data-card-embed"),
            collapsed: el.hasAttribute("data-collapsed"),
            height: height > 0 ? height : null,
          };
        },
      },
    ],
    toDOM: (node) => {
      const attrs: Record<string, string> = { "data-card-embed": node.attrs["uuid"] as string };
      if (node.attrs["collapsed"]) attrs["data-collapsed"] = "";
      if (node.attrs["height"] != null) attrs["data-height"] = String(node.attrs["height"]);
      return ["div", attrs];
    },
  },
};

const marks: Record<string, MarkSpec> = {
  bold: {
    parseDOM: [{ tag: "strong" }, { tag: "b" }, { style: "font-weight=bold" }],
    toDOM: () => ["strong", 0],
  },
  italic: {
    parseDOM: [{ tag: "em" }, { tag: "i" }, { style: "font-style=italic" }],
    toDOM: () => ["em", 0],
  },
  strikethrough: {
    parseDOM: [{ tag: "s" }, { tag: "del" }],
    toDOM: () => ["s", 0],
  },
  superscript: {
    parseDOM: [{ tag: "sup" }],
    toDOM: () => ["sup", 0],
  },
  subscript: {
    parseDOM: [{ tag: "sub" }],
    toDOM: () => ["sub", 0],
  },
  code: {
    parseDOM: [{ tag: "code" }],
    toDOM: () => ["code", 0],
  },
  // External hyperlink: marked text whose `href` is the target. Distinct from
  // `card_link` (an atom node for internal links, rendered as a[data-card-link]).
  link: {
    attrs: { href: {} },
    inclusive: false,
    parseDOM: [
      {
        tag: "a[href]",
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          // Don't swallow internal card links (separate node type).
          if (el.hasAttribute("data-card-link")) return false;
          return { href: el.getAttribute("href") };
        },
      },
    ],
    toDOM: (mark) => [
      "a",
      { href: mark.attrs["href"] as string, class: "external-link", rel: "noopener", target: "_blank" },
      0,
    ],
  },
};

export const schema = new Schema({ nodes, marks });
